import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  CheckCircle2,
  Layers3,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { authService } from "@/features/auth/services/authService";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { cn, useTheme } from "@/ui/theme/ThemeContext";
import type {
  PaymentPlanReadiness,
  PaymentReadinessResponse,
} from "@/shared/apiTypes";
import type { SignupPlanId } from "./components/AuthEntryShell";
import {
  clearPendingSignupRecovery,
  getStoredValue,
  PENDING_SIGNUP_PLAN_STORAGE_KEY,
  PENDING_SIGNUP_RETURN_TO_STORAGE_KEY,
  PENDING_SIGNUP_USER_ID_STORAGE_KEY,
} from "./billingSetupRecovery";

const planCatalog: Record<SignupPlanId, { name: string; price: string; detail: string }> = {
  free: {
    name: "Free",
    price: "$0",
    detail: "Workspace access without paid entitlements.",
  },
  pro: {
    name: "Pro",
    price: "$20/account/month",
    detail: "Selected for focused self-service workspaces.",
  },
  ultra: {
    name: "Ultra",
    price: "$200/account/month",
    detail: "Selected for higher usage and priority.",
  },
};

const planVisuals: Record<SignupPlanId, { icon: LucideIcon; accent: string; capacity: string }> = {
  free: {
    icon: Layers3,
    accent: "from-zinc-500 to-slate-400",
    capacity: "Starter workspace",
  },
  pro: {
    icon: Sparkles,
    accent: "from-blue-500 via-cyan-400 to-emerald-400",
    capacity: "Recommended launch plan",
  },
  ultra: {
    icon: BadgeDollarSign,
    accent: "from-violet-500 via-blue-400 to-amber-300",
    capacity: "Priority operator capacity",
  },
};

const normalizePlan = (rawPlan: string | null | undefined): SignupPlanId | null => {
  if (rawPlan === "free" || rawPlan === "pro" || rawPlan === "ultra") {
    return rawPlan;
  }

  return null;
};

const sanitizeReturnPath = (rawReturnTo: string | null): string => {
  if (rawReturnTo && rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")) {
    return rawReturnTo;
  }

  return "/";
};

const blockerLabels: Record<string, string> = {
  payments_mode_shadow: "Payment mode is still shadow",
  stripe_secret_key_missing: "Stripe secret key",
  stripe_webhook_secret_missing: "Stripe webhook secret",
  stripe_pro_price_id_missing: "Stripe Pro price",
  stripe_ultra_price_id_missing: "Stripe Ultra price",
  stripe_billing_portal_configuration_id_missing: "Stripe billing portal configuration",
  stripe_not_configured: "Stripe provider values",
  stripe_price_lookup_failed_400: "Stripe price lookup",
  stripe_price_lookup_failed_401: "Stripe API authentication",
  stripe_price_lookup_failed_403: "Stripe API authorization",
  stripe_price_lookup_failed_404: "Stripe price not found",
  stripe_price_not_active: "Stripe price is inactive",
  stripe_price_currency_not_usd: "Stripe price must be USD",
  stripe_price_not_monthly: "Stripe price must be monthly",
  stripe_price_amount_mismatch: "Stripe price amount mismatch",
  toss_billing_contract_not_approved: "Toss billing contract approval",
  toss_billing_client_key_missing: "Toss billing client key",
  toss_billing_secret_key_missing: "Toss billing secret key",
  toss_billing_merchant_id_missing: "Toss billing merchant ID",
  toss_billing_auto_charge_disabled: "Toss first charge is disabled",
  toss_pro_krw_amount_missing: "Toss Pro KRW amount",
  toss_ultra_krw_amount_missing: "Toss Ultra KRW amount",
  toss_api_timeout_too_low: "Toss API timeout must allow billing approval",
};

const readinessProviderConfigured = (readiness: PaymentPlanReadiness | null | undefined): boolean =>
  Boolean(readiness?.providerConfigured ?? readiness?.provider_configured);

const readinessCheckoutModeEnabled = (readiness: PaymentPlanReadiness | null | undefined): boolean =>
  Boolean(readiness?.checkoutModeEnabled ?? readiness?.checkout_mode_enabled);

export const BillingSetupPage: React.FC = () => {
  const { t } = useSettings();
  const { theme } = useTheme();
  const { actualUser, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentReadiness, setPaymentReadiness] = useState<PaymentReadinessResponse | null>(null);
  const [readinessError, setReadinessError] = useState("");
  const [isReadinessLoading, setIsReadinessLoading] = useState(true);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const recoveryBelongsToUser = !isLoading && isAuthenticated && Boolean(actualUser?.id)
    && getStoredValue(PENDING_SIGNUP_USER_ID_STORAGE_KEY) === actualUser?.id;
  const requestedPlan = normalizePlan(params.get("plan"))
    ?? (recoveryBelongsToUser ? normalizePlan(getStoredValue(PENDING_SIGNUP_PLAN_STORAGE_KEY)) : null);
  const savedPlan = actualUser?.selfServicePlan;
  const persistedPlan = normalizePlan(savedPlan?.selectedTier ?? savedPlan?.selected_tier ?? savedPlan?.tier);
  const selectedPlan = requestedPlan ?? persistedPlan;
  const preferenceIsSaved = selectedPlan !== null && selectedPlan === persistedPlan;
  const returnPath = sanitizeReturnPath(
    params.get("returnTo")
      || (recoveryBelongsToUser ? getStoredValue(PENDING_SIGNUP_RETURN_TO_STORAGE_KEY) : null)
  );
  const plan = selectedPlan ? planCatalog[selectedPlan] : {
    name: "No plan selected", price: "", detail: "",
  };
  const planVisual = planVisuals[selectedPlan ?? "free"];
  const PlanIcon = planVisual.icon;
  const planReadiness =
    !selectedPlan || selectedPlan === "free" ? null : paymentReadiness?.readiness?.plans?.[selectedPlan] ?? null;
  const checkoutConfigured =
    Boolean(selectedPlan) && selectedPlan !== "free"
    && readinessCheckoutModeEnabled(planReadiness)
    && readinessProviderConfigured(planReadiness);
  const blockers = planReadiness?.blockers ?? [];
  const statusLabel = checkoutConfigured ? "Provider config ignored in shadow" : "Shadow mode";

  useEffect(() => {
    if (!isLoading && !recoveryBelongsToUser) clearPendingSignupRecovery();
  }, [isLoading, recoveryBelongsToUser, actualUser?.id]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    let isMounted = true;
    setIsReadinessLoading(true);
    setPaymentReadiness(null);
    setReadinessError("");
    authService.getPaymentReadiness()
      .then((readiness) => {
        if (isMounted) {
          setPaymentReadiness(readiness);
          setReadinessError("");
        }
      })
      .catch((error) => {
        if (isMounted) {
          setReadinessError(
            error instanceof Error ? error.message : t("Could not load payment readiness")
          );
        }
      })
      .finally(() => {
        if (isMounted) setIsReadinessLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading, actualUser?.id, t]);

  if (isLoading) {
    return (
      <div
        data-app-ready="false"
        className={cn(
          "flex min-h-screen items-center justify-center overflow-x-hidden px-5 py-8",
          theme.bgPrimary,
          theme.textPrimary
        )}
      >
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" aria-hidden="true" />
          <p className={cn("mt-4 text-sm", theme.textSecondary)}>{t("Checking your session")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/signup?returnTo=${encodeURIComponent(returnPath)}${selectedPlan ? `&plan=${selectedPlan}` : ""}`} replace />;
  }

  return (
    <div
      data-app-ready="true"
      data-active-view="billing-setup"
      className="flex min-h-screen items-center justify-center overflow-x-hidden bg-[linear-gradient(135deg,#f8fafc_0%,#eef2ff_42%,#ecfeff_100%)] px-5 py-8 text-zinc-950 dark:bg-[linear-gradient(135deg,#121212_0%,#171717_50%,#0f1a19_100%)] dark:text-white sm:px-8"
    >
      <main className="w-full max-w-[840px] min-w-0">
        <section
          className="relative min-w-0 overflow-hidden rounded-lg border border-zinc-200/80 bg-white/[0.78] p-5 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/[0.10] dark:bg-white/[0.06] dark:shadow-[0_28px_110px_rgba(0,0,0,0.34)] sm:p-7 lg:p-8"
          aria-labelledby="billing-setup-title"
        >
          <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", planVisual.accent)} aria-hidden="true" />
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={cn(
                  "flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-[0_14px_34px_rgba(37,99,235,0.22)]",
                  planVisual.accent
                )}
                aria-hidden="true"
              >
                <PlanIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-zinc-950 dark:text-white">
                  {t("Management Tool")}
                </p>
                <p className="text-xs text-zinc-600 dark:text-zinc-400">
                  {t("Plan preference")}
                </p>
              </div>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-md border border-amber-300/60 bg-amber-100/80 px-3 py-2 text-sm font-semibold text-amber-900 shadow-[0_10px_26px_rgba(217,119,6,0.10)] dark:border-amber-300/20 dark:bg-amber-300/[0.12] dark:text-amber-100">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(statusLabel)}</span>
            </div>
          </div>

          <div className="mt-6">
            <h1 id="billing-setup-title" className="text-3xl font-semibold leading-tight text-zinc-950 dark:text-white sm:text-4xl">
              {t(preferenceIsSaved ? "Your plan preference is saved" : "Your requested plan")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-700 dark:text-zinc-300 sm:text-base sm:leading-7">
              {t("CommerceOS is connected as a shadow payment layer. No checkout opens, no card is charged, and paid entitlements are not enabled.")}
              {" "}
              <span>{t("This selection does not change your saved account preference.")}</span>
              {" "}
              <Link
                className="font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
                to="/legal/refund-policy"
              >
                {t("Review payment and refund policy")}
              </Link>
            </p>
          </div>

          <div className="mt-7 border-y border-zinc-200/80 py-5 dark:border-white/[0.10]">
            <div className="grid min-w-0 gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={cn(
                    "mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-[0_10px_28px_rgba(15,23,42,0.16)]",
                    planVisual.accent
                  )}
                  aria-hidden="true"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-600 dark:text-zinc-400">{t("Selected plan")}</p>
                  <div className="mt-1 flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="text-3xl font-semibold text-zinc-950 dark:text-white">{t(plan.name)}</p>
                    <p className="text-sm font-semibold text-blue-700 dark:text-cyan-300">{t(plan.price)}</p>
                  </div>
                  <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{selectedPlan ? t(planVisual.capacity) : null}</p>
                  <p className="mt-2 text-sm leading-6 text-zinc-700 dark:text-zinc-300">{t(plan.detail)}</p>
                </div>
              </div>
              {selectedPlan ? <div className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-md border border-emerald-300/50 bg-emerald-100/80 px-3 text-sm font-semibold text-emerald-900 shadow-[0_10px_26px_rgba(16,185,129,0.10)] dark:border-emerald-300/20 dark:bg-emerald-300/10 dark:text-emerald-100">
                <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{t(preferenceIsSaved ? "Preference recorded" : "Requested preference")}</span>
              </div> : null}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex min-w-0 items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-cyan-300" aria-hidden="true" />
              <p className="text-sm leading-6 text-zinc-700 dark:text-zinc-300">
                {t("Checkout stays off until CommerceOS registration, provider credentials, webhook proof, owner binding, and deployment settings are all real.")}
              </p>
            </div>
            {selectedPlan && selectedPlan !== "free" && !readinessError && (isReadinessLoading || !planReadiness) ? (
              <p className="mt-3 text-sm" role="status">
                {t(isReadinessLoading ? "Loading payment readiness" : "Payment readiness is unavailable")}
              </p>
            ) : null}
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {blockers.map((blocker) => (
                <li
                  key={blocker}
                  className="flex min-h-[38px] min-w-0 items-center gap-2 rounded-md border border-zinc-200/80 bg-white/[0.55] px-3 text-sm text-zinc-700 shadow-[0_8px_22px_rgba(15,23,42,0.04)] dark:border-white/[0.10] dark:bg-white/[0.045] dark:text-zinc-300"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-300" aria-hidden="true" />
                  <span className="min-w-0 truncate">{t(blockerLabels[blocker] || blocker)}</span>
                </li>
              ))}
            </ul>
            {readinessError ? (
              <p className="mt-3 text-sm text-red-500" role="alert">{readinessError}</p>
            ) : null}
          </div>

          <div className="mt-7 grid gap-3 sm:grid-cols-[1fr_auto]">
            <button
              type="button"
              onClick={() => {
                clearPendingSignupRecovery();
                navigate(returnPath, { replace: true });
              }}
              className="inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-md bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] transition-all duration-300 hover:shadow-[0_18px_44px_rgba(6,182,212,0.34)] focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950"
            >
              {t("Continue to workspace")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md border border-zinc-300/80 bg-white/70 px-3 text-sm font-semibold text-zinc-700 dark:border-white/[0.14] dark:bg-white/[0.05] dark:text-zinc-300">
              <Zap className="h-4 w-4 text-amber-500 dark:text-amber-300" aria-hidden="true" />
              {t("Checkout off")}
            </span>
          </div>

          <div className="mt-5 border-t border-zinc-200/80 pt-4 text-sm text-zinc-600 dark:border-white/[0.10] dark:text-zinc-400">
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              {t("Enterprise stays contact-sales.")}
            </span>{" "}
            <a
              className="font-semibold text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
              href="mailto:sales@dope-ai.kr?subject=Management%20Tool%20Enterprise"
            >
              {t("Contact sales")}
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};
